import Konva from "konva";
import { useAtomValue } from "jotai";
import { useMemo, useState } from "react";
import { Group, Path, Rect, Text } from "react-konva";
import { posScaleAtom } from "state-atoms/stage-atoms";
import { setMousePointer, unsetMouse } from "utils/mouse-cursor-utils";
import { ExcludeFrom } from "frontend/exclude-from";

const KONVA_PADDING = 10;
const WIDTH = 20;
const HEIGHT = 20;

const MAX_TOOLTIP_WIDTH = 400;

export function CanvasElementLinkBadgeKonva({
  url,
  scaleX,
  scaleY,
  x,
  y,
  onClick,
}: {
  url: string;
  x: number;
  y: number;
  scaleX: number;
  scaleY: number;
  onClick: () => void;
}) {
  const scale = useAtomValue(posScaleAtom).scale;
  const [isHover, setIsHover] = useState(false);

  const { text, textWidth } = useMemo(() => {
    const textNode = new Konva.Text({
      x: 0,
      y: 0,
      text: url,
      fontFamily: "Poppins",
      fontSize: 12,
      fontVariant: "300",
      wrap: "none",
      ellipsis: true,
      width: MAX_TOOLTIP_WIDTH, // Set max width for ellipsis calculation
    });

    // Calculate the actual width needed for the text (up to MAX_TOOLTIP_WIDTH)
    const actualWidth = Math.min(textNode.getTextWidth(), MAX_TOOLTIP_WIDTH);

    return {
      text: textNode,
      textWidth: actualWidth,
    };
  }, [url]);

  const konvaX = 0 + x - WIDTH / scaleX / scale - KONVA_PADDING / scaleX;
  const konvaY = 0 + y - HEIGHT / scaleY / scale - KONVA_PADDING / scaleY;

  return (
    <ExcludeFrom fromTransformer={true} fromExports={true}>
      <Group
        x={konvaX}
        y={konvaY}
        scaleX={1 / scaleX / scale}
        scaleY={1 / scaleY / scale}
        onMouseEnter={(e) => {
          setMousePointer(e);
          setIsHover(true);
        }}
        onMouseLeave={(e) => {
          unsetMouse(e);
          setIsHover(false);
        }}
        onClick={(e) => {
          e.cancelBubble = true;
          setIsHover(false);
          onClick();
        }}
      >
        <Rect width={WIDTH} height={HEIGHT} fill="white" shadowColor="black" shadowBlur={20} shadowOpacity={0.1} />
        <Group x={WIDTH - 16} y={HEIGHT - 16}>
          <Path
            data="M4.75537 7.24446C4.95814 7.45142 5.20018 7.61583 5.46729 7.72807C5.7344 7.84031 6.02122 7.89813 6.31096 7.89813C6.6007 7.89813 6.88752 7.84031 7.15463 7.72807C7.42174 7.61583 7.66377 7.45142 7.86655 7.24446L10.3555 4.75552C10.7681 4.34295 10.9998 3.78339 10.9998 3.19993C10.9998 2.61648 10.7681 2.05691 10.3555 1.64435C9.94292 1.23178 9.38336 1 8.7999 1C8.21644 1 7.65688 1.23178 7.24431 1.64435L6.93319 1.95546"
            stroke="#848199"
            strokeWidth={1}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <Path
            data="M7.24446 4.75553C7.04169 4.54858 6.79966 4.38417 6.53255 4.27192C6.26544 4.15968 5.97861 4.10187 5.68888 4.10187C5.39914 4.10187 5.11232 4.15968 4.8452 4.27192C4.57809 4.38417 4.33606 4.54858 4.13329 4.75553L1.64435 7.24447C1.23178 7.65704 1 8.2166 1 8.80006C1 9.38352 1.23178 9.94308 1.64435 10.3557C2.05691 10.7682 2.61648 11 3.19993 11C3.78339 11 4.34295 10.7682 4.75552 10.3557L5.06664 10.0445"
            stroke="#848199"
            strokeWidth={1}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Group>
        {isHover && (
          <Group x={0} y={30}>
            <Rect
              ref={(e) => e && ((e as any).getSelfRect = () => ({ x: 0, y: 0, width: 0, height: 0 }))}
              x={0}
              y={0}
              width={textWidth + KONVA_PADDING}
              height={24}
              fill="#fafafa"
              cornerRadius={4}
              shadowColor="rgba(0, 0, 0, 0.2)"
              shadowOffset={{ x: 0, y: 2 }}
              shadowOpacity={1}
              listening={false}
            />
            <Text
              ref={(e) => e && ((e as any).getSelfRect = () => ({ x: 0, y: 0, width: 0, height: 0 }))}
              x={KONVA_PADDING / 2}
              y={KONVA_PADDING / 2}
              text={url}
              fontFamily="Poppins"
              fontSize={text.fontSize()}
              fontVariant="300"
              width={textWidth}
              height={12}
              wrap="none"
              ellipsis={true}
              align="left"
              verticalAlign="middle"
              listening={false}
            />
          </Group>
        )}
      </Group>
    </ExcludeFrom>
  );
}
